import { store } from "../../store";
import { persistTourFlags, setTourFlags } from "../../store/userSlice";

// Tour flags live on the User record, one Boolean field each, so progress follows a user across
// devices and browsers. Mirrored in Redux at permissionDetails — the single copy these helpers
// read and write. Reads are non-reactive snapshots of the store — a component calling getTourFlag
// in render will NOT re-render when a flag changes; gate render-time use behind local state or
// effects (see welcomeShownState / chatTourStarted in VerticalNav).
// onboardingGuidanceSent gates the AI onboarding coaching so a user only ever gets it once,
// independent of whether they ran, skipped, or never opened the chat tour.
export type TourFlag =
  | "welcomeShown"
  | "chatTourCompleted"
  | "groupTourSeen"
  | "assetTourSeen"
  | "onboardingGuidanceSent";

const ALL_TOUR_FLAGS: TourFlag[] = [
  "welcomeShown",
  "chatTourCompleted",
  "groupTourSeen",
  "assetTourSeen",
  "onboardingGuidanceSent",
];

// Each flag is its own field on the record, so a write touches only that attribute — two tabs or
// devices finishing different tours can't clobber each other, and writes need no ordering or merge.
export const TOUR_FLAG_FIELD: Record<TourFlag, string> = {
  welcomeShown: "tourWelcomeShown",
  chatTourCompleted: "tourChatCompleted",
  groupTourSeen: "tourGroupSeen",
  assetTourSeen: "tourAssetSeen",
  onboardingGuidanceSent: "tourOnboardingGuidanceSent",
};

// The loaded User record, as the tour sees it — userSlice itself is untyped (@ts-nocheck).
type TourUserRecord = Record<string, unknown> & { id?: string };

// Reads straight off the Redux copy of the record — no second copy to fall out of sync.
// Null until that record is loaded, so a miss is never mistaken for "nothing seen".
const readRecord = (userId: string): TourUserRecord | null => {
  const permissionDetails: TourUserRecord | undefined = store.getState().user?.permissionDetails;
  if (permissionDetails?.id !== userId) return null;
  return permissionDetails;
};

// Writes Redux first so reads see the flag immediately, then sends only the changed fields.
// Resolves false when the mutation failed — never rejects, so awaiting callers can still proceed.
const persistFlags = async (userId: string, fields: Record<string, boolean>): Promise<boolean> => {
  store.dispatch(setTourFlags({ userId, fields }));
  const result = await store.dispatch(persistTourFlags({ id: userId, fields }));
  return !persistTourFlags.rejected.match(result);
};

export const getTourFlag = (userId: string | null | undefined, flag: TourFlag): boolean => {
  if (!userId) return false;
  return readRecord(userId)?.[TOUR_FLAG_FIELD[flag]] === true;
};

export const setTourFlagStored = (userId: string | null | undefined, flag: TourFlag): void => {
  if (!userId) return;
  const record = readRecord(userId);
  if (!record) return; // not this user's record — don't write to it
  if (record[TOUR_FLAG_FIELD[flag]] === true) return; // already recorded — skip a redundant mutation
  void persistFlags(userId, { [TOUR_FLAG_FIELD[flag]]: true });
};

// Phrases that read like someone new to the platform — matched case-insensitively.
const NEW_USER_INTENT_PATTERNS: RegExp[] = [
  /\bhow (do|can) i (get )?start/i,
  /\b(getting|get) started\b/i,
  /\bwhere (do|should) i (begin|start)\b/i,
  /\bwhat can (you|this|it|erode|the platform) do\b/i,
  /\bwhat (is|does) (this|erode|the platform)\b/i,
  /\bhow (does|do) (this|you|it|erode|the platform) work\b/i,
  /\bi('?m| am) new\b/i,
  /\bnew (here|to (this|the platform|erode))\b/i,
  /\bhelp me get started\b/i,
  /\bwhat should i (do|ask)\b/i,
];

// True when a message reads like it's from someone new to the platform (intent onboarding).
export const looksLikeNewUser = (text: string): boolean => {
  const trimmed = text.trim();
  if (!trimmed) return false;
  return NEW_USER_INTENT_PATTERNS.some((re) => re.test(trimmed));
};

export interface OnboardingDecisionInput {
  userId: string | null | undefined;
  tourActive: boolean; // chat tour currently running
  text: string;
  isFirstEverMessage: boolean; // no prior conversations or messages on the account
  isEstablishedUser: boolean; // more than one prior conversation — a returning user
  isFirstMessageInConversation: boolean; // no prior messages in the current conversation
}

// Decides whether to attach AI onboarding guidance to a send. Fires at most once per user
// (guarded by the onboardingGuidanceSent flag): during the chat tour, on a brand-new user's
// first-ever message (deferred, for those who skipped the tour), or when a not-yet-established
// user's message reads like they're new (intent). Established users never trigger the last two.
export const shouldSendOnboardingGuidance = (input: OnboardingDecisionInput): boolean => {
  const { userId, tourActive, text, isFirstEverMessage, isEstablishedUser, isFirstMessageInConversation } = input;
  if (getTourFlag(userId, "onboardingGuidanceSent")) return false;
  if (tourActive) return true;
  if (isFirstEverMessage) return true;
  if (!isEstablishedUser && isFirstMessageInConversation && looksLikeNewUser(text)) return true;
  return false;
};

// Clears every tour flag for a user so the whole first-run flow replays (admin restart).
// Async now that progress is server-side — await it before reloading or the write is aborted.
// Resolves false when the backend write failed, since a reload would then restore the old flags.
export const clearTourFlags = async (userId: string | null | undefined): Promise<boolean> => {
  if (!userId) return false;
  const cleared = ALL_TOUR_FLAGS.reduce<Record<string, boolean>>((acc, flag) => {
    acc[TOUR_FLAG_FIELD[flag]] = false;
    return acc;
  }, {});
  return persistFlags(userId, cleared);
};
